import {
  ChangeDetectionStrategy,
  afterRenderEffect,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DiscussionFormComponent } from './discussion-form/discussion-form.component';
import { DiscussionEmptyStateComponent } from './discussion-empty-state/discussion-empty-state.component';
import { DiscussionCommentViewComponent } from './discussion-comment-view/discussion-comment-view.component';
import {
  DiscussionComment,
  DiscussionCommentRequest,
  DiscussionCommentResponse,
  EmojiLookup,
  UserReaction,
} from './request-discussion.model';
import { RequestDiscussionService } from './services/request-discussion/request-discussion.service';
import { Channel } from 'pusher-js';
import { FormControl, FormGroup } from '@angular/forms';
import { delay, of, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { DiscussionBase } from '../base/discussion-base';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { Attachment } from '../../../../../ui-components/lib/upload-files/attachment.interface';
import { LoaderComponent } from '../../../../../ui-components/lib/loader/loader.component';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { PusherService } from '../../../../common/services/pusher/pusher.service';
import { FormService } from '../../../../../form-builder/services/form/form.service';
import {
  FeatureFlagsService,
  FEATURE_FLAGS,
} from 'all-libraries/ibusiness-core/src/core/common';

@Component({
  selector: 'lib-request-discussion',
  imports: [
    DiscussionFormComponent,
    DiscussionEmptyStateComponent,
    DiscussionCommentViewComponent,
    LoaderComponent,
  ],
  templateUrl: './request-discussion.component.html',
  styleUrl: './request-discussion.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestDiscussionComponent implements OnInit, OnDestroy {
  private readonly requestDiscussionService = inject(RequestDiscussionService);
  private readonly pusherService = inject(PusherService);
  private readonly toaster = inject(ToasterService);
  private readonly formService = inject(FormService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly discussionBase = new DiscussionBase();
  private readonly featureFlagsService = inject(FeatureFlagsService);

  isRequestHeaderUiEnhancementEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.workflow_header_ui_enhancement
    )
  );
  commentIdToScroll =
    this.activatedRoute.snapshot.queryParams[
      this.discussionBase.DISCUSSION_COMMENT_ID_QUERY_PARAM
    ];

  commentsContainer =
    viewChild<ElementRef<HTMLDivElement>>('commentsContainer');

  flowId = input<string>('');
  newComment = input<DiscussionComment>();
  deletedCommentId = input<number>();
  showAttachmentsOnly = input<boolean>(false);

  turnOffOutSideClick = output<boolean>();

  comments = signal<DiscussionComment[]>([]);
  isLoading = signal<boolean>(false);
  clearTimeout = signal<any>(null);
  nextCursor = signal<string>('');
  isNearBottom = signal<boolean>(true);
  canLoadMore = signal<boolean>(false); // to prevent loading more comments on initial load
  discussionFormGroup = signal<FormGroup>(
    new FormGroup({
      content: new FormControl(''),
      attachments: new FormControl(null),
      mentioned_users: new FormControl(null),
    })
  );
  isDeleteCommentLoading = signal<boolean>(false);
  isUpdateCommentLoading = signal<boolean>(false);
  selectedCommentId = signal<number>(0);
  initialAttachments = signal<Attachment[]>([]);
  reactionList = signal<EmojiLookup[]>([]);
  commentReactions = signal<UserReaction[]>([]);
  commentReactionsMeta = signal<Meta>({
    current_page: 1,
    from: 0,
    last_page: 0,
    links: [],
    path: '',
    per_page: 0,
    to: 0,
    total: 0,
  });
  triggerClearForm = signal<boolean>(false);
  showAllReactions = signal<boolean>(false);
  isReactionsLoading = signal<boolean>(false);

  subscription = new Subscription();

  private channel: Channel | null = null;
  private channelName = '';

  constructor() {
    this.handleNewComment();
    this.handleDeletedComment();
  }

  handleNewComment(): void {
    afterRenderEffect(() => {
      this.handleNewCommentsPushed();
    });
  }

  handleNewCommentsPushed(): void {
    if (
      this.newComment() &&
      !this.comments().some(comment => comment.id === this.newComment()?.id) &&
      ((this.showAttachmentsOnly() && this.newComment()?.attachments.length) ||
        !this.showAttachmentsOnly())
    ) {
      if (this.comments().length > 0) {
        this.comments.update(oldComments => [
          ...oldComments,
          this.newComment() as DiscussionComment,
        ]);
        this.scrollToBottom();
      } else {
        this.comments.set([this.newComment() as DiscussionComment]);
      }
    }
  }

  handleDeletedComment(): void {
    afterRenderEffect(() => {
      if (this.deletedCommentId()) {
        this.comments.update(comments =>
          comments.filter(comment => comment.id !== this.deletedCommentId())
        );
      }
    });
  }

  ngOnInit(): void {
    if (this.showAttachmentsOnly()) {
      this.getAttachments();
    } else {
      this.getComments();
      this.getReactionList();
    }
  }

  scrollToComment(commentId: number): void {
    const commentElement = document.getElementById(`comment-${commentId}`);
    if (commentElement) {
      commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      commentElement.classList.add('mention-comment');
    }
  }

  getComments(nextCursor = ''): void {
    this.isLoading.set(true);
    this.subscription.add(
      this.requestDiscussionService
        .getComments(this.flowId(), nextCursor)
        .subscribe({
          next: response => this.handleCommentsResponse(response),
          error: () => this.isLoading.set(false),
        })
    );
  }

  getAttachments(nextCursor = ''): void {
    this.isLoading.set(true);
    this.subscription.add(
      this.requestDiscussionService
        .getAttachments(this.flowId(), nextCursor)
        .subscribe({
          next: response => this.handleCommentsResponse(response),
          error: () => this.isLoading.set(false),
        })
    );
  }

  handleCommentsResponse(response: DiscussionCommentResponse): void {
    const oldComments = response.data.reverse();
    const filteredComments = this.showAttachmentsOnly()
      ? oldComments.filter(comment => comment.attachments.length > 0)
      : oldComments;
    if (this.nextCursor()) {
      this.comments.update(newComments => [
        ...filteredComments,
        ...(this.showAttachmentsOnly()
          ? newComments.filter(comment => comment.attachments.length > 0)
          : newComments),
      ]);
    } else {
      this.comments.set(filteredComments);
      if (this.isNearBottom()) {
        this.scrollToBottom(100);
      }
    }
    if (this.commentIdToScroll) {
      of(null)
        .pipe(delay(1000))
        .subscribe(() => this.scrollToComment(this.commentIdToScroll));
    }
    this.nextCursor.set(response.meta.next_cursor || '');
    this.isLoading.set(false);
  }

  getReactionList(): void {
    this.subscription.add(
      this.requestDiscussionService.getEmojiLookup().subscribe(response => {
        this.reactionList.set(response.data);
      })
    );
  }

  addComment(body: DiscussionCommentRequest): void {
    this.isUpdateCommentLoading.set(true);
    this.subscription.add(
      this.requestDiscussionService
        .addComment(this.flowId(), body)
        .subscribe({
          next: () => {
            this.isUpdateCommentLoading.set(false);
            this.triggerClearForm.set(true);
          },
          error: error => this.handleSubmitCommentError(error),
        })
        .add(() =>
          this.subscription.add(
            of(null)
              .pipe(delay(100))
              .subscribe(() => this.triggerClearForm.set(false))
          )
        )
    );
  }

  fillForm(comment: DiscussionComment): void {
    this.discussionFormGroup().patchValue({
      content: comment.content,
      attachments: comment.attachments.map(attachment => attachment.id),
      mentioned_users: comment.mentioned_users?.map(user => user.id) ?? [],
    });
    this.selectedCommentId.set(comment.id);
    this.initialAttachments.set(
      comment.attachments.map(attachment => ({
        id: attachment.id,
        name: attachment.name || '',
        url: attachment.url || '',
        size: attachment.size || 0,
      }))
    );
  }

  updateComment(body: DiscussionCommentRequest): void {
    this.isUpdateCommentLoading.set(true);
    const commentId = this.selectedCommentId().toString();
    this.subscription.add(
      this.requestDiscussionService
        .updateComment(this.flowId(), commentId, body)
        .subscribe({
          next: () => {
            this.isUpdateCommentLoading.set(false);
            this.triggerClearForm.set(true);
          },
          error: error => this.handleSubmitCommentError(error),
        })
        .add(() =>
          this.subscription.add(
            of(null)
              .pipe(delay(100))
              .subscribe(() => this.triggerClearForm.set(false))
          )
        )
    );
  }

  handleSubmitCommentError(error: any): void {
    this.isUpdateCommentLoading.set(false);
    this.toaster.add(
      error.message ? error.message : error.error?.message,
      '',
      '',
      'error',
      true,
      true
    );
    this.formService.addControlErrors(error, this.discussionFormGroup());
  }

  discardForm(): void {
    this.selectedCommentId.set(0);
    this.initialAttachments.set([]);
    this.discussionFormGroup().reset();
  }

  markAsRead(): void {
    this.requestDiscussionService.markAsRead(this.flowId());
  }

  scrollToBottom(delay = 1): void {
    this.clearTimeout.set(
      setTimeout(() => {
        const threshold = 50;
        const commentsContainer = <ElementRef<HTMLDivElement>>(
          this.commentsContainer()
        );
        const { scrollHeight, scrollTop, clientHeight } =
          commentsContainer.nativeElement;
        commentsContainer.nativeElement.scrollTo({
          top: commentsContainer.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
        const position = scrollTop + clientHeight;
        this.isNearBottom.set(scrollHeight - position <= threshold);
        this.clearTimeout.set(
          setTimeout(() => {
            this.canLoadMore.set(true);
          }, 100)
        );
      }, delay)
    );
  }

  onScrollTop(event: Event): void {
    const { scrollTop } = event.target as unknown as {
      scrollTop: number;
    };
    if (
      scrollTop <= 200 &&
      !this.isLoading() &&
      !!this.nextCursor() &&
      this.canLoadMore()
    ) {
      if (this.showAttachmentsOnly()) {
        this.getAttachments(this.nextCursor());
      } else {
        this.getComments(this.nextCursor());
      }
    }
  }

  deleteComment(comment: DiscussionComment): void {
    this.isDeleteCommentLoading.set(true);
    this.subscription.add(
      this.requestDiscussionService
        .deleteComment(this.flowId(), comment.id.toString())
        .subscribe({
          next: () => {
            this.isDeleteCommentLoading.set(false);
          },
          error: error => {
            this.isDeleteCommentLoading.set(false);
            this.toaster.add(
              error.message ? error.message : error.error?.message,
              '',
              '',
              'error',
              true,
              true
            );
          },
        })
    );
  }

  storeReact(dependencies: { comment: DiscussionComment; slug: string }): void {
    this.subscription.add(
      this.requestDiscussionService
        .storeReaction(
          this.flowId(),
          dependencies.comment.id.toString(),
          dependencies.slug
        )
        .subscribe()
    );
  }

  deleteReact(comment: DiscussionComment): void {
    this.subscription.add(
      this.requestDiscussionService
        .deleteReaction(this.flowId(), comment.id.toString())
        .subscribe()
    );
  }

  getReactions(body: { comment: DiscussionComment; emojiSlug: string }): void {
    this.isReactionsLoading.set(true);
    this.subscription.add(
      this.requestDiscussionService
        .getReactions(this.flowId(), body.comment.id.toString(), body.emojiSlug)
        .subscribe({
          next: (res: ApiResponse<UserReaction>) => {
            this.commentReactions.set(res.data);
            this.commentReactionsMeta.set(res.meta);
            this.isReactionsLoading.set(false);
          },
        })
    );
  }

  toggleUserReactions(body: {
    showAllReactions: boolean;
    comment: DiscussionComment;
  }): void {
    this.showAllReactions.set(body.showAllReactions);
    if (this.showAllReactions()) {
      this.getReactions({ comment: body.comment, emojiSlug: '' });
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.clearTimeout());
    this.clearTimeout.set(null);

    if (this.channel && this.channelName) {
      this.channel.unbind('comment.created');
      this.pusherService.unsubscribe(this.channelName);
    }
    this.subscription.unsubscribe();
  }
}
